import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';
import {
  DcEventMetric,
  DcScheduleDetails,
  DcScheduleEventDetail,
  DcScheduleRow
} from '../../domain/dc-schedule.model';
import { CollectorEligibilityContext } from '../../domain/user';
import { AlertService } from '../../services/alert.service';
import { DcRosterService } from '../../services/dc-roster.service';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { getHttpErrorMessage } from '../../shared/http-error-message.util';
import {
  ScheduleEventHistoryDialogComponent,
  ScheduleEventHistoryDialogData
} from '../schedule-event-history-dialog/schedule-event-history-dialog.component';
import { scheduleProgressTooltip } from '../schedule-progress.util';

@Component({
  selector: 'app-schedule-roster-details',
  templateUrl: './schedule-roster-details.component.html',
  styleUrls: ['./schedule-roster-details.component.css']
})
export class ScheduleRosterDetailsComponent implements OnInit, OnChanges {
  @Input() row: DcScheduleRow;
  @Input() hasWritePermission = false;
  @Input() canMarkAssigned = false;
  @Input() markingAssigned = false;

  @Output() detailsChange = new EventEmitter<DcScheduleDetails>();
  @Output() rosterDeleted = new EventEmitter<string>();
  @Output() markAssigned = new EventEmitter<void>();

  details: DcScheduleDetails | null = null;
  loading = true;
  errorMessage = '';
  actionInFlight: 'clear' | 'delete' | null = null;
  mutatingEventIds = new Set<string>();

  constructor(
    private dcRosterService: DcRosterService,
    private alertService: AlertService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    void this.loadDetails();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.row || changes.row.firstChange || !this.details) {
      return;
    }
    const previousGameId = changes.row.previousValue?.gameId;
    if (previousGameId !== this.row.gameId) {
      this.details = null;
      void this.loadDetails();
      return;
    }
    this.syncTeamLeaderFromRow();
  }

  async loadDetails(): Promise<void> {
    try {
      this.loading = true;
      this.errorMessage = '';
      this.details = await firstValueFrom(
        this.dcRosterService.getScheduleDetails(this.row.gameId)
      );
      this.syncTeamLeaderFromRow();
    } catch (error) {
      this.errorMessage =
        'Could not load DC roster details: ' +
        (await getHttpErrorMessage(error));
    } finally {
      this.loading = false;
    }
  }

  async onCollectorChanged(
    event: DcScheduleEventDetail,
    auth0Id: string | null
  ): Promise<void> {
    if (!this.hasWritePermission || this.isEventMutating(event.eventId)) {
      return;
    }
    const nextCollectorAuthId = auth0Id?.trim() || null;
    if (nextCollectorAuthId === event.collectorAuthId) {
      return;
    }

    const previousDetails = this.details;
    this.optimisticallySetCollector(event.eventId, nextCollectorAuthId);
    this.mutatingEventIds.add(event.eventId);
    try {
      const updated = await firstValueFrom(
        this.dcRosterService.updateScheduleEventCollector(
          event.eventId,
          nextCollectorAuthId
        )
      );
      this.applyDetails(updated);
    } catch (error) {
      this.details = previousDetails;
      this.alertService.showError(
        'Could not update collector: ' + (await getHttpErrorMessage(error))
      );
    } finally {
      this.mutatingEventIds.delete(event.eventId);
    }
  }

  openHistory(event: DcScheduleEventDetail): void {
    const data: ScheduleEventHistoryDialogData = {
      eventId: event.eventId,
      code: event.code
    };
    this.dialog.open(ScheduleEventHistoryDialogComponent, {
      autoFocus: false,
      data,
      width: '720px',
      maxWidth: 'calc(100vw - 32px)'
    });
  }

  async confirmClearRoster(): Promise<void> {
    if (!this.hasWritePermission || this.actionInFlight) {
      return;
    }
    const confirmed = await firstValueFrom(
      this.dialog
        .open(ConfirmationDialogComponent, {
          data: {
            title: 'Clear DC Roster',
            message:
              'Remove every collector assignment from this DC roster? The roster, events, and their history will remain.',
            okButtonCaption: 'Clear Roster'
          }
        })
        .afterClosed()
    );
    if (!confirmed) {
      return;
    }

    try {
      this.actionInFlight = 'clear';
      const updated = await firstValueFrom(
        this.dcRosterService.clearScheduleRoster(this.row.gameId)
      );
      this.applyDetails(updated);
    } catch (error) {
      this.alertService.showError(
        'Could not clear roster: ' + (await getHttpErrorMessage(error))
      );
    } finally {
      this.actionInFlight = null;
    }
  }

  async confirmDeleteRoster(): Promise<void> {
    if (!this.hasWritePermission || this.actionInFlight) {
      return;
    }
    const confirmed = await firstValueFrom(
      this.dialog
        .open(ConfirmationDialogComponent, {
          data: {
            title: 'Delete DC Roster',
            message:
              'The DC roster and all related DC events will be permanently deleted. The game will remain.',
            okButtonCaption: 'Delete Roster'
          }
        })
        .afterClosed()
    );
    if (!confirmed) {
      return;
    }

    try {
      this.actionInFlight = 'delete';
      await firstValueFrom(
        this.dcRosterService.deleteScheduleRoster(this.row.gameId)
      );
      this.rosterDeleted.emit(this.row.gameId);
    } catch (error) {
      this.alertService.showError(
        'Could not delete roster: ' + (await getHttpErrorMessage(error))
      );
    } finally {
      this.actionInFlight = null;
    }
  }

  requestMarkAssigned(): void {
    if (this.hasWritePermission && this.canMarkAssigned) {
      this.markAssigned.emit();
    }
  }

  isEventMutating(eventId: string): boolean {
    return this.mutatingEventIds.has(eventId);
  }

  metricPercentage(metric: DcEventMetric): number | null {
    return metric.kind === 'percentage' || metric.kind === 'status'
      ? metric.percentage
      : null;
  }

  metricLabel(metric: DcEventMetric): string {
    if (metric.kind === 'unmapped') {
      return 'N/A';
    }
    if (metric.kind === 'count') {
      return metric.count + ' ' + metric.label;
    }
    const percentage = Math.round(metric.percentage) + '%';
    if (
      metric.kind === 'percentage' &&
      metric.numerator !== undefined &&
      metric.denominator !== undefined
    ) {
      return metric.numerator + '/' + metric.denominator + ' · ' + percentage;
    }
    return percentage;
  }

  overallProgressTooltip(): string {
    return scheduleProgressTooltip(
      this.details?.progressPercentage ?? this.row.progressPercentage,
      this.details?.progressBreakdown ?? this.row.progressBreakdown
    );
  }

  lastInputLabel(event: DcScheduleEventDetail): string {
    if (event.lastInput.kind === 'not_applicable') {
      return 'N/A';
    }
    if (event.lastInput.kind === 'none') {
      return 'No input';
    }
    const at = DateTime.fromISO(event.lastInput.at);
    if (!at.isValid) {
      return 'No input';
    }
    const seconds = Math.max(
      0,
      Math.floor(DateTime.now().diff(at, 'seconds').seconds)
    );
    if (seconds < 60) {
      return 'Just now';
    }
    if (seconds < 3600) {
      return Math.floor(seconds / 60) + ' min ago';
    }
    if (seconds < 86400) {
      return Math.floor(seconds / 3600) + ' h ago';
    }
    return Math.floor(seconds / 86400) + ' d ago';
  }

  lastInputTooltip(event: DcScheduleEventDetail): string {
    return event.lastInput.kind === 'timestamp' ? event.lastInput.at : '';
  }

  getCollectorEligibilityContext(
    event: DcScheduleEventDetail,
    currentEventIndex: number
  ): CollectorEligibilityContext {
    const events = this.details?.events ?? [];
    return {
      gameId: this.row.gameId,
      eventCode: event.code,
      eventId: event.eventId,
      dcDate: this.row.dcDate,
      dcTime: this.row.dcTime,
      dcType: this.row.dcType,
      dcSubtype: this.row.dcSubtype,
      gmTeamLeaderAuthId: this.details?.teamLeaderAuthId ?? null,
      currentEventIndex,
      draftRoster: {
        gameId: this.row.gameId,
        dcDate: this.row.dcDate,
        dcTime: this.row.dcTime,
        dcType: this.row.dcType,
        dcSubtype: this.row.dcSubtype,
        collectionEvents: events.map((collectionEvent) => ({
          _id: collectionEvent.eventId,
          code: collectionEvent.code,
          collectorAuthId: collectionEvent.collectorAuthId,
          dcTypeOverride: null,
          status: collectionEvent.status
        }))
      }
    };
  }

  trackByEventId(_index: number, event: DcScheduleEventDetail): string {
    return event.eventId;
  }

  private applyDetails(details: DcScheduleDetails): void {
    this.details = details;
    this.detailsChange.emit(details);
  }

  private optimisticallySetCollector(
    eventId: string,
    collectorAuthId: string | null
  ): void {
    if (!this.details) {
      return;
    }
    const previousEvent = this.details.events.find(
      (event) => event.eventId === eventId
    );
    if (!previousEvent) {
      return;
    }
    const assignedDelta =
      (collectorAuthId ? 1 : 0) - (previousEvent.collectorAuthId ? 1 : 0);
    const isGm = previousEvent.code.trim().toUpperCase() === 'GM';
    this.details = {
      ...this.details,
      rosterAssigned: Math.max(
        0,
        Math.min(
          this.details.rosterTotal,
          this.details.rosterAssigned + assignedDelta
        )
      ),
      teamLeaderAuthId: isGm ? collectorAuthId : this.details.teamLeaderAuthId,
      events: this.details.events.map((event) =>
        event.eventId === eventId
          ? {
              ...event,
              collectorAuthId,
              collectorName: collectorAuthId,
              status: null
            }
          : event
      )
    };
  }

  private syncTeamLeaderFromRow(): void {
    if (!this.details) {
      return;
    }
    const teamLeaderAuthId = this.row.teamLeaderAuthId ?? null;
    this.details = {
      ...this.details,
      rosterAssigned: this.row.rosterAssigned,
      rosterTotal: this.row.rosterTotal,
      teamLeaderAuthId,
      progressPercentage: this.row.progressPercentage,
      progressBreakdown: this.row.progressBreakdown,
      events: this.details.events.map((event) =>
        event.code.trim().toUpperCase() === 'GM'
          ? {
              ...event,
              collectorAuthId: teamLeaderAuthId,
              collectorName:
                event.collectorAuthId === teamLeaderAuthId &&
                event.collectorName
                  ? event.collectorName
                  : this.row.teamLeaderName ?? teamLeaderAuthId,
              status:
                event.collectorAuthId === teamLeaderAuthId ? event.status : null
            }
          : event
      )
    };
  }
}
